import { Box, Typography } from '@mui/material';
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';
import HubOutlinedIcon from '@mui/icons-material/HubOutlined';
import AutoStoriesOutlinedIcon from '@mui/icons-material/AutoStoriesOutlined';
import {
  deleteCreditsTeamCarveApi,
  deleteCreditsUserAllowanceApi,
  grantCreditsSeatApi,
  revokeCreditsSeatApi,
  setCreditsAllocationApi,
  setCreditsTeamCarveApi,
  setCreditsUserAllowanceApi,
} from '../../../services/api';
import {
  MODES,
  orgMembers,
  pendingKey,
  READ_ONLY_SEAT_PRODUCTS,
  seatSummary,
  storedCap,
  TEAM_CARVE_PRODUCTS,
  usedFor,
} from '../utils';
import AllocationPanel from './AllocationPanel';
import CapTable from './CapTable';
import SeatsPanel from './SeatsPanel';
import { BORDER, cardSx, INK, MUTED, pillSx, SOFT, titleSx } from './styles';

const ICONS = { queenbee: VideocamOutlinedIcon, smarthub: HubOutlinedIcon, storybuilder: AutoStoriesOutlinedIcon };

const blockSx = { display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 };

// One card per product: allocation, team carves (QueenBee, shared pool),
// user allowances (per user), and seats.
export default function ProductCard({ product, label, data, run, pending, askConfirm }) {
  const allocation = data?.allocation?.[product];
  const seats = data?.seats?.[product];
  const mode = allocation?.mode || MODES.SHARED;
  const members = orgMembers(data);
  const teams = data?.teams || [];

  const showCarves = !!allocation && TEAM_CARVE_PRODUCTS.includes(product) && mode === MODES.SHARED;
  const showAllowances = !!allocation && mode === MODES.PER_USER;

  const defaultAllowance = allocation?.defaultUserAllowance ?? null;
  const readOnlySeats = READ_ONLY_SEAT_PRODUCTS.includes(product);
  const Icon = ICONS[product] || HubOutlinedIcon;

  const teamRows = teams.map((team) => {
    const cap = storedCap(data, product, 'team', team.id);
    return {
      id: team.id,
      label: team.name,
      used: usedFor(data, product, 'team', team.id),
      cap,
      limit: cap,
    };
  });

  const userRows = members.map((user) => {
    const cap = storedCap(data, product, 'user', user.id);
    return {
      id: user.id,
      label: user.name,
      secondary: user.email,
      used: usedFor(data, product, 'user', user.id),
      cap,
      limit: cap ?? defaultAllowance,
      placeholder: defaultAllowance === null ? 'No cap' : `Default ${defaultAllowance}`,
    };
  });

  const hasManage = !!allocation;
  const twoColumns = hasManage && !!seats;

  return (
    <Box sx={{ ...cardSx, p: 0, overflow: 'hidden' }} data-testid={`product-card-${product}`}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1.5,
          flexWrap: 'wrap',
          px: { xs: '16px', md: '22px' },
          py: { xs: '14px', md: '16px' },
          borderBottom: `1px solid ${BORDER}`,
          backgroundColor: '#FCFCFB',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
          <Box sx={{ width: 38, height: 38, borderRadius: '10px', backgroundColor: SOFT, border: `1px solid ${BORDER}`, display: 'grid', placeItems: 'center', flexShrink: 0 }}>
            <Icon sx={{ fontSize: 20, color: INK }} />
          </Box>
          <Typography sx={titleSx}>{label}</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
          {allocation && (
            <Box component="span" sx={pillSx}>
              <Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: 'primary.main' }} />
              {mode === MODES.PER_USER ? 'Per-user allowances' : 'Shared pool'}
            </Box>
          )}
          {seats && (
            <Box component="span" sx={{ ...pillSx, color: MUTED }}>
              {seatSummary(seats, readOnlySeats).heading.replace(/^Seats: /, '')}
            </Box>
          )}
        </Box>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: twoColumns ? 'minmax(0, 1.6fr) minmax(0, 1fr)' : 'minmax(0, 1fr)' },
          columnGap: 4,
          rowGap: 3,
          p: { xs: '16px', md: '20px 22px' },
        }}
      >
        {hasManage && (
          <Box sx={blockSx}>
            <AllocationPanel
              product={product}
              allocation={allocation}
              busy={!!pending[pendingKey.alloc(product)]}
              onSave={(body) => run(pendingKey.alloc(product), () => setCreditsAllocationApi({ product, body }))}
            />

            {showCarves && (
              <CapTable
                testId={`team-carves-${product}`}
                heading="Team carves"
                description="Reserve part of the pool for a team. Teams without a carve share what is left."
                nameColumn="Team"
                capColumn="Cap per period"
                rows={teamRows}
                emptyHint="This organisation has no teams yet. Create a team to reserve part of the pool for it."
                isBusy={(teamId) => !!pending[pendingKey.team(product, teamId)]}
                onSave={(row, cap) =>
                  run(pendingKey.team(product, row.id), () => setCreditsTeamCarveApi({ product, teamId: row.id, capPerPeriod: cap }))
                }
                onRemove={(row) =>
                  askConfirm({
                    title: 'Remove team carve?',
                    message: `${row.label} will draw from the shared pool again with no reserved credits.`,
                    confirmLabel: 'Remove',
                    onConfirm: () =>
                      run(pendingKey.team(product, row.id), () => deleteCreditsTeamCarveApi({ product, teamId: row.id })),
                  })
                }
              />
            )}

            {showAllowances && (
              <CapTable
                testId={`user-allowances-${product}`}
                heading={`User allowances (default ${allocation?.defaultUserAllowance ?? '—'})`}
                description="Leave a field empty to use the default. An override applies to that member only."
                nameColumn="Member"
                capColumn="Override cap per period"
                rows={userRows}
                avatars
                emptyHint="There are no active members in this organisation yet."
                isBusy={(userId) => !!pending[pendingKey.user(product, userId)]}
                onSave={(row, cap) =>
                  run(pendingKey.user(product, row.id), () => setCreditsUserAllowanceApi({ product, userId: row.id, capPerPeriod: cap }))
                }
                onRemove={(row) =>
                  askConfirm({
                    title: 'Remove allowance override?',
                    message: `${row.label} will go back to the default allowance.`,
                    confirmLabel: 'Remove',
                    onConfirm: () =>
                      run(pendingKey.user(product, row.id), () => deleteCreditsUserAllowanceApi({ product, userId: row.id })),
                  })
                }
              />
            )}
          </Box>
        )}

        {seats && (
          <Box
            sx={{
              ...blockSx,
              ...(twoColumns && {
                borderTop: { xs: `1px solid ${BORDER}`, lg: 0 },
                borderLeft: { lg: `1px solid ${BORDER}` },
                pt: { xs: 3, lg: 0 },
                pl: { lg: 4 },
              }),
            }}
          >
            <SeatsPanel
              product={product}
              seats={seats}
              members={members}
              readOnly={readOnlySeats}
              isGrantBusy={!!pending[pendingKey.grant(product)]}
              isRevokeBusy={(userId) => !!pending[pendingKey.revoke(product, userId)]}
              onGrant={(member, done) =>
                run(pendingKey.grant(product), () => grantCreditsSeatApi({ product, userId: member.id })).then(done)
              }
              onRevoke={(holder) =>
                askConfirm({
                  title: `Revoke ${label} seat?`,
                  message: `${holder.name} will lose access to ${label}.`,
                  confirmLabel: 'Revoke',
                  onConfirm: () =>
                    run(pendingKey.revoke(product, holder.id), () => revokeCreditsSeatApi({ product, userId: holder.id })),
                })
              }
            />
          </Box>
        )}
      </Box>
    </Box>
  );
}
